import React, { useCallback, useContext, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";

import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import StatusBadge from "../../../components/StatusBadge";
import Spinner from "../../../components/Spinner";
import EmptyState from "../../../components/EmptyState";
import { useTableLang } from "../../../utils/tableLang";
import { getStores, updateStoreActivity } from "../../../api/storesApi";
import { LanguageContext } from "../../../components/LanguageContext";

export const StoresPage = () => {
    const tableLang = useTableLang();
    const queryClient = useQueryClient();
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const { translations } = useContext(LanguageContext);

    const { data, isLoading } = useQuery({
        queryKey: ["stores"],
        queryFn: () => getStores(),
        staleTime: 5 * 60 * 1000,
    });

    const mutationActivity = useMutation({
        mutationFn: (formData) => updateStoreActivity(formData),
        onSuccess: (resp) => {
            setError("");
            queryClient.invalidateQueries(["stores"]);
            setSuccess(resp.data.message);
        },
        onError: (err) => {
            setSuccess("");
            setError(err.response?.data?.message || "");
        },
    });

    const handleChangeActivity = useCallback(
        (event, store_code) => {
            event.preventDefault();
            const formData = new FormData();
            formData.append("store_code", store_code);
            mutationActivity.mutateAsync(formData).catch(() => {});
        },
        [mutationActivity]
    );

    const columns = useMemo(
        () => [
            {
                field: "id",
                headerName: translations?.adminStoresPageStoreId || "ID",
                width: 80,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "code",
                headerName:
                    translations?.adminStoresPageStoreCode || "Šifra prodavnice",
                minWidth: 160,
                flex: 1,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        variant="outlined"
                        sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                        }}
                    />
                ),
            },
            {
                field: "name",
                headerName:
                    translations?.adminStoresPageStoreName || "Naziv prodavnice",
                minWidth: 240,
                flex: 2,
                renderCell: (params) => (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                            sx={(theme) => ({
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor:
                                    theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.04)"
                                        : "rgba(15,23,42,0.04)",
                                color: "primary.main",
                            })}
                        >
                            <StorefrontOutlinedIcon fontSize="small" />
                        </Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                            title={params.value}
                        >
                            {params.value}
                        </Typography>
                    </Stack>
                ),
            },
            {
                field: "active",
                headerName: translations?.active || "Aktivna",
                minWidth: 200,
                flex: 1,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => {
                    const isActive = Number(params.row.active) === 1;
                    return (
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            justifyContent="center"
                            sx={{ width: "100%" }}
                        >
                            <StatusBadge
                                status={isActive ? "active" : "inactive"}
                                label={
                                    isActive
                                        ? translations?.activeStore || "Aktivna"
                                        : translations?.disabledStore || "Neaktivna"
                                }
                            />
                            <Switch
                                size="small"
                                checked={isActive}
                                onChange={(e) =>
                                    handleChangeActivity(e, params.row.code)
                                }
                            />
                        </Stack>
                    );
                },
            },
            {
                field: "info",
                headerName: translations?.adminStoresPageStoreInfo || "Informacije",
                minWidth: 160,
                flex: 0.8,
                sortable: false,
                filterable: false,
                hideable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => {
                    const isActive = Number(params.row.active) === 1;
                    return (
                        <Tooltip
                            title={
                                isActive
                                    ? translations?.adminStoresPageStoreInfo ||
                                      "Informacije"
                                    : translations?.disabledStore || "Prodavnica nije aktivna"
                            }
                        >
                            <span>
                                <Button
                                    component={isActive ? RouterLink : "button"}
                                    to={isActive ? `/prodavnica/${params.row.code}` : undefined}
                                    disabled={!isActive}
                                    variant="text"
                                    size="small"
                                    endIcon={<ChevronRightRoundedIcon />}
                                >
                                    {translations?.adminStoresPageStoreInfo ||
                                        "Informacije"}
                                </Button>
                            </span>
                        </Tooltip>
                    );
                },
            },
        ],
        [translations, handleChangeActivity]
    );

    const rows = useMemo(() => data?.data || [], [data]);

    return (
        <Box>
            <PageHeader
                icon={<StorefrontOutlinedIcon />}
                eyebrow={translations?.adminAdministratorMenuStores || "Stores"}
                title={
                    translations?.adminStoresPageStoreName || "Prodavnice"
                }
                description={
                    translations?.storesSubtitle ||
                    "Pregled i upravljanje prodavnicama u sistemu."
                }
            />

            <Stack spacing={2}>
                {success && (
                    <Alert severity="success" onClose={() => setSuccess("")}>
                        {success}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <SectionCard padded={false}>
                    {isLoading ? (
                        <Spinner variant="inline" />
                    ) : rows.length === 0 ? (
                        <EmptyState
                            icon={<StorefrontOutlinedIcon />}
                            title={translations?.noStoresTitle || "Nema prodavnica"}
                            description={
                                translations?.noStoresDescription ||
                                "Trenutno nema prodavnica u sistemu."
                            }
                        />
                    ) : (
                        <Box sx={{ position: "relative" }}>
                            {mutationActivity.isLoading && (
                                <Spinner variant="overlay" />
                            )}
                            <Box sx={{ height: 720, width: "100%" }}>
                                <DataGrid
                                    slots={{ toolbar: GridToolbar }}
                                    rows={rows}
                                    columns={columns}
                                    initialState={{
                                        pagination: {
                                            paginationModel: { page: 0, pageSize: 25 },
                                        },
                                    }}
                                    localeText={tableLang}
                                    pageSizeOptions={[25, 50, 100]}
                                    disableRowSelectionOnClick
                                    getRowHeight={() => 64}
                                    sx={(theme) => ({
                                        border: "none",
                                        "& .MuiDataGrid-columnHeaders": {
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                            backgroundColor:
                                                theme.palette.mode === "dark"
                                                    ? "rgba(255,255,255,0.02)"
                                                    : "rgba(15,23,42,0.02)",
                                        },
                                        "& .MuiDataGrid-columnHeaderTitle": {
                                            fontWeight: 600,
                                            fontSize: "0.8rem",
                                            color: "text.secondary",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.05em",
                                        },
                                        "& .MuiDataGrid-cell": {
                                            display: "flex",
                                            alignItems: "center",
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                        },
                                        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                                            { outline: "none" },
                                        "& .MuiDataGrid-row:hover": {
                                            backgroundColor: theme.palette.action.hover,
                                        },
                                        "& .MuiDataGrid-footerContainer": {
                                            borderTop: `1px solid ${theme.palette.divider}`,
                                        },
                                        "& .MuiDataGrid-toolbarContainer": {
                                            p: 1.5,
                                            gap: 1,
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                        },
                                    })}
                                />
                            </Box>
                        </Box>
                    )}
                </SectionCard>
            </Stack>
        </Box>
    );
};